/**
 * O que faz: Monta o dashboard principal com filtros, cards, gráficos e modais de detalhamento.
 * Onde usa: Tela principal do dashboard da clínica e visão administrativa.
 * Conexões: DashboardHeader, DashboardMetrics, DashboardCharts, LeadsListModal, hooks de dados e exportação.
 * Tabelas: Usa dados agregados da tabela leads e relacionamentos carregados pelos hooks do dashboard.
 * Data: 19/05/2026 (ordem das seções e design system Gens em 05/10/2026; cartão "Tempo até fechar" em 05/10/2026;
 *       cartão "Leads para retomar hoje" em 22/10/2026).
 */

import { Suspense, useCallback, useEffect, useState } from 'react';
import { carregarModuloComRetry } from '@/lib/recarregarAposFalhaDeChunk';
import { FalhaCarregamentoChunk } from '@/components/layout/FalhaCarregamentoChunk';
import { DashboardHeader } from './DashboardHeader';
import { DashboardMetrics } from './DashboardMetrics';
import { SecaoResultadosMetricas } from './SecaoResultadosMetricas';
import { DashboardCharts } from './DashboardCharts';
import type { OrigemMetaClicada } from './AdPerformanceCard';
import { DashboardInsights } from './DashboardInsights';
import { LeadsListModal } from './LeadsListModal';
import { DashboardExportModal } from './DashboardExportModal'; // NOVO: Modal de exportação
import { useDashboardConsultas } from '@/hooks/dashboard/useDashboardConsultas';
import { hojeNoCalendarioSaoPaulo } from '@/features/dashboard/data/dashboardContracts';
import type { FiltroContatosDashboard } from '@/features/dashboard/data/dashboardContracts';
import type { FiltroSemanaDashboard } from '@/features/dashboard/data/dashboardSeries';
import { useAdAliases } from '@/hooks/useAdAliases';
import { useDashboardExport } from '@/hooks/useDashboardExport'; // NOVO: Hook de exportação
import { useClinica } from '@/contexts/ClinicaContext';
import { useNavigate } from 'react-router-dom';
import { ResponseTimeCard } from './ResponseTimeCard';
import { SecaoTempoFechamentoDashboard } from './SecaoTempoFechamentoDashboard';
import { CartaoRetomadaDashboard } from './CartaoRetomadaDashboard';
import { LinkAnaliseConversas } from './LinkAnaliseConversas';
import { ConversionAttributionNotice } from './ConversionAttributionNotice';
import type { FiltroAcaoPrioritaria } from './AcoesPrioritariasDashboard';
import { startOfDay, endOfDay } from 'date-fns';
import type { ReactNode } from 'react';

// O detalhamento financeiro é carregado somente ao abrir o card, sem pesar no dashboard inicial.
const FaturamentoRealizadoModal = carregarModuloComRetry(() => import('./FaturamentoRealizadoModal'));

/**
 * Componente principal do Dashboard
 * 
 * MELHORIAS:
 * - Filtros de fuso horário unificados para SP (-3 UTC)
 * - Contagem consistente entre card e modal de "Leads de Anúncios"
 * - Exibição limpa de apelidos (sem redundância)
 * - NOVO: Suporte para links personalizados com drill-down
 * - NOVO: Card de "Leads de Links Personalizados" com modal específico
 * - Suporte completo para modo admin com apelidos
 * - Funcionalidade de exportação PDF integrada
 * 
 * Resumos agregados alimentam cards/gráficos/PDF; dados pessoais só são buscados ao abrir detalhes.
 */

interface DashboardProps {
  adminMode?: boolean;
  targetClinicaId?: string;
  clinicName?: string | null;
  headerActions?: ReactNode;
}

const normalizarNomeAnuncio = (nome: string): string =>
  nome.trim().toLowerCase().replace(/\s+/g, ' ');

export const Dashboard = ({ adminMode = false, targetClinicaId, clinicName, headerActions }: DashboardProps) => {
  const navigate = useNavigate();
  // ALTERADO: Definir "Hoje" como seleção padrão (era "Este Mês")
  const [startDate, setStartDate] = useState<Date | null>(() => startOfDay(hojeNoCalendarioSaoPaulo()));
  const [endDate, setEndDate] = useState<Date | null>(() => endOfDay(hojeNoCalendarioSaoPaulo()));
  const [currentFilter, setCurrentFilter] = useState('Hoje');
  // O filtro semanal é visual e independente do período; manter o estado aqui
  // permite que o card troque entre todos os leads e somente anúncios sem nova query.
  const [filtroSemanal, setFiltroSemanal] = useState<FiltroSemanaDashboard>('todos');

  // Estados para controle dos modais de drill-down  
  // Mantém um modal dedicado para "Total de Contatos" para repetir a UX já usada nos cards de origem.
  // Motivo: o usuário precisa entrar na lista do dia com um clique sem navegar para outra tela.
  const [totalContatosModalOpen, setTotalContatosModalOpen] = useState(false);
  const [faturamentoModalOpen, setFaturamentoModalOpen] = useState(false);
  const [leadsAnunciosModalOpen, setLeadsAnunciosModalOpen] = useState(false);
  const [adSpecificModalOpen, setAdSpecificModalOpen] = useState(false);
  const [selectedAdName, setSelectedAdName] = useState<string | null>(null);

  // Estados para modal de links personalizados
  const [linkSpecificModalOpen, setLinkSpecificModalOpen] = useState(false);
  const [selectedLinkName, setSelectedLinkName] = useState<string | null>(null);
  // Conjunto/campanha clicado no card de anúncios (abas do Meta): abre o mesmo modal de leads.
  const [origemMetaSelecionada, setOrigemMetaSelecionada] = useState<OrigemMetaClicada | null>(null);

  // NOVO: Estados para modal de links personalizados gerais
  const [leadsLinksModalOpen, setLeadsLinksModalOpen] = useState(false);

  // NOVO: Hook para exportação PDF
  const {
    isModalOpen: isExportModalOpen,
    selectedFormat,
    isExporting,
    generatedAt,
    openExportModal,
    closeExportModal,
    changeFormat,
    exportDashboard,
  } = useDashboardExport();

  const { clinicaAtiva, clinicaId } = useClinica();

  // Uma clínica efetiva monta duas consultas analíticas independentes, sem download integral.
  const { contexto, resumo, atendimento, dados: dashboardData } = useDashboardConsultas(
    adminMode ? targetClinicaId : clinicaId, startDate, endDate, adminMode ? 'admin' : 'clinica',
  );
  const { error, refetch } = resumo;
  const isLoading = resumo.isLoading || !contexto;
  const atendimentoError = atendimento.error;
  const isAtendimentoLoading = atendimento.isLoading;
  const refetchAtendimento = atendimento.refetch;
  const isRefreshing = resumo.isFetching || atendimento.isFetching;
  // Refetch do mesmo período mantém ações do snapshot; placeholder de outro período não.
  const isBaseDataStale = resumo.isPlaceholderData || !!error || !dashboardData;
  const isExportBlocked = isBaseDataStale || isAtendimentoLoading || !!atendimentoError;
  const periodoExibido = resumo.isPlaceholderData && resumo.data
    ? (resumo.data.periodo.inicio ? `${resumo.data.periodo.inicio} a ${resumo.data.periodo.fim}` : 'Máximo')
    : currentFilter;

  // CORRIGIDO: Usar hook de apelidos com targetClinicaId para modo admin
  const { getAliasForAd } = useAdAliases(adminMode ? targetClinicaId : undefined);

  const handleFilterChange = (newStartDate: Date | null, newEndDate: Date | null, filterName: string) => {
    // Fecha detalhes e preview antes de trocar o período para não misturar dados antigos
    // com o novo rótulo enquanto as queries cancelam a página anterior.
    handleCloseModals();
    setFaturamentoModalOpen(false);
    closeExportModal();
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setCurrentFilter(filterName);
  };

  // Atualiza as métricas usando a mesma query já existente para evitar duplicar regra de
  // busca e garantir que o botão respeite cache, loading e tratamento de erro do React Query.
  const handleRefreshMetrics = async () => {
    // Atualiza resumo e atendimento juntos, mas cada um mantém seu próprio erro e loading.
    await Promise.allSettled([refetch(), refetchAtendimento()]);
  };

  const handlePriorityAction = (filtro: FiltroAcaoPrioritaria) => {
    // Separa cada recomendação da navegação de anúncios e preserva a clínica ao abrir conversas em modo admin.
    const destinos: Record<FiltroAcaoPrioritaria, string> = {
      'sem-resposta': '/chat',
      falhas: '/follow-up',
      'retornos-vencidos': '/retornos',
    };
    const parametrosClinica = filtro === 'sem-resposta' && adminMode && targetClinicaId
      ? `?adminClinicId=${encodeURIComponent(targetClinicaId)}`
      : '';
    navigate(`${destinos[filtro]}${parametrosClinica}`);
  };

  // O detalhe recebe um filtro, não uma cópia dos contatos do período.
  const filtroDetalhe: FiltroContatosDashboard = origemMetaSelecionada ? { tipo: origemMetaSelecionada.dimensao, chave: origemMetaSelecionada.chave }
    : selectedAdName ? { tipo: 'anuncio', chave: normalizarNomeAnuncio(selectedAdName) }
    : selectedLinkName ? { tipo: 'link', chave: selectedLinkName.trim().toLowerCase() }
    : leadsAnunciosModalOpen ? { tipo: 'anuncios' } : leadsLinksModalOpen ? { tipo: 'links_exclusivos' } : { tipo: 'todos' };

  const totalDoDetalhe = origemMetaSelecionada ? origemMetaSelecionada.leads : selectedAdName
    ? dashboardData?.resumo?.anuncios.itens.find(item => item.chave === normalizarNomeAnuncio(selectedAdName))?.leads
      ?? dashboardData?.leadsPorAnuncio.find(item => normalizarNomeAnuncio(item.anuncio) === normalizarNomeAnuncio(selectedAdName))?.leads
    : selectedLinkName
      ? dashboardData?.resumo?.links.itens.find(item => item.chave === selectedLinkName.trim().toLowerCase())?.leads
        ?? dashboardData?.leadsPorLink.find(item => item.linkName.trim().toLowerCase() === selectedLinkName.trim().toLowerCase())?.leads
      : undefined;

  // CORRIGIDO: Função para obter nome de exibição do anúncio (APENAS apelido quando disponível)
  const getDisplayAdName = useCallback((adName: string | null) => {
    if (!adName) return 'Não informado';

    const alias = getAliasForAd(adName);
    const resultado = alias || adName; // Apelido tem prioridade, fallback para nome original

    return resultado;
  }, [getAliasForAd]);

  // Funções para abrir os modais de drill-down
  // Abre a mesma experiência de lista + chat usada nos demais cards.
  // Regra de negócio: o card "Total de Contatos" deve permitir inspeção imediata dos contatos do filtro ativo.
  const handleTotalContatosClick = () => {
    setTotalContatosModalOpen(true);
  };

  const handleLeadsAnunciosClick = () => {
    setLeadsAnunciosModalOpen(true);
  };

  // NOVA: Função para abrir modal de leads de links personalizados
  const handleLeadsLinksClick = () => {
    setLeadsLinksModalOpen(true);
  };

  const handleAdSpecificClick = (adName: string) => {
    // Definir estado e abrir modal (limpa um conjunto/campanha que tenha ficado selecionado).
    setOrigemMetaSelecionada(null);
    setSelectedAdName(adName);
    setAdSpecificModalOpen(true);
  };

  // Clique numa linha de conjunto/campanha: reaproveita o modal de anúncio específico (mesma lista + chat).
  const handleOrigemMetaClick = (origem: OrigemMetaClicada) => {
    setSelectedAdName(null);
    setOrigemMetaSelecionada(origem);
    setAdSpecificModalOpen(true);
  };

  // NOVO: Função para lidar com clique em link específico
  const handleLinkSpecificClick = (linkName: string) => {
    // Definir estado e abrir modal
    setSelectedLinkName(linkName);
    setLinkSpecificModalOpen(true);
  };

  // Função para fechar modais e limpar estado
  const handleCloseModals = () => {
    setTotalContatosModalOpen(false);
    setLeadsAnunciosModalOpen(false);
    setAdSpecificModalOpen(false);
    setLinkSpecificModalOpen(false);
    setLeadsLinksModalOpen(false); // NOVO: Fechar modal de links gerais
    setSelectedAdName(null);
    setSelectedLinkName(null);
    setOrigemMetaSelecionada(null);
  };

  useEffect(() => {
    // Seleção e preview não sobrevivem à mudança de usuário, clínica ou modo.
    handleCloseModals();
    setFaturamentoModalOpen(false);
    closeExportModal();
    // Os callbacks apenas limpam estados; o efeito responde à identidade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contexto?.usuarioId, contexto?.clinicaId, contexto?.modo]);

  // CORRIGIDO: Função para obter nome da clínica para exportação
  const getClinicaNameForExport = () => {
    // Prioriza o nome disponível para que o PDF não perca a identidade da clínica fora da visão admin.
    if (clinicName?.trim()) return clinicName.trim();
    if (clinicaAtiva?.nome?.trim()) return clinicaAtiva.nome.trim();
    if (adminMode && targetClinicaId) {
      // Em modo admin, usar ID parcial como fallback simples
      return `Clínica ${targetClinicaId.slice(0, 8)}`;
    }
    return 'Clínica';
  };

  // Ajusta o texto do modal para refletir claramente o período ativo.
  // Motivo: evita confusão quando o usuário troca o filtro e espera ver exatamente o mesmo recorte no detalhe.
  const totalContatosModalTitle = currentFilter === 'Hoje'
    ? 'Contatos de Hoje'
    : `Contatos - ${currentFilter}`;

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Header da página com o filtro de período e botão de exportação — SEMPRE visível */}
      <DashboardHeader
        onFilterChange={handleFilterChange}
        currentFilter={currentFilter}
        onRefreshClick={handleRefreshMetrics}
        isRefreshing={isRefreshing}
        onExportClick={isExportBlocked ? undefined : openExportModal}
        clinicName={clinicName}
        actions={headerActions}
        // Ao lado de "Atualizar": antes ficava em cima do card de tempo de resposta e desalinhava a grade.
        acoesPeriodo={<LinkAnaliseConversas adminClinicId={adminMode && targetClinicaId ? targetClinicaId : undefined} />}
      />

      {resumo.isPlaceholderData && <p role="status" className="text-sm text-muted-foreground">
        Atualizando para {currentFilter}; exibindo {periodoExibido}.
      </p>}

      {/* Mensagem de erro (não bloqueia o layout) */}
      {error && (
        <div className="bg-danger-subtle border border-danger-text/20 rounded-2xl p-4 text-danger-text text-sm">
          Erro ao carregar dados do dashboard. Tente atualizar os dados.
        </div>
      )}
      {atendimentoError && !error && (
        <div role="status" className="rounded-2xl border border-warning/30 bg-warning-subtle p-4 text-sm text-warning-text">
          Os indicadores de atendimento ainda não estão disponíveis. Os demais dados continuam utilizáveis.
        </div>
      )}

      {/* Lista de trabalho do dia, no topo e independente do período escolhido: mostra quem precisa de
          retomada manual hoje (pedido do Marcolino em 22/10/2026). Consulta própria e leve, não atrasa os KPIs. */}
      <CartaoRetomadaDashboard clinicaId={adminMode ? targetClinicaId : clinicaId} adminMode={adminMode} />

      {/* Grid de cartões de métricas com ResponseTime */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Métricas principais — skeleton local durante primeira carga, dados suaves em atualizações */}
        {/* h-full propaga a altura da coluna (esticada pelo grid) até o grid interno de KPIs,
            permitindo que a última linha de cards alinhe com a base do ResponseTimeCard. */}
        <div className="col-span-1 lg:col-span-3 h-full">
          {isLoading && !dashboardData ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="bg-card rounded-2xl p-4 sm:p-5 border border-border animate-pulse">
                  <div className="h-3.5 bg-muted rounded w-3/4 mb-4"></div>
                  <div className="h-7 bg-muted rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <DashboardMetrics
              dashboardData={dashboardData}
              onTotalContatosClick={isBaseDataStale ? undefined : handleTotalContatosClick}
              onLeadsAnunciosClick={isBaseDataStale ? undefined : handleLeadsAnunciosClick}
              onLeadsLinksClick={isBaseDataStale ? undefined : handleLeadsLinksClick}
              // Durante troca de período o card ainda exibe o cache anterior; aguarda a consulta correta.
              onFaturamentoClick={isBaseDataStale ? undefined : () => setFaturamentoModalOpen(true)}
              isUpdating={isBaseDataStale}
              atendimentoLoading={isAtendimentoLoading}
              atendimentoError={!!atendimentoError}
            />
          )}
        </div>

        {/* Card de Tempo de Resposta */}
        <div className="col-span-1 flex flex-col">
          {isLoading && !dashboardData ? (
            <div className="bg-card rounded-2xl p-4 sm:p-5 border border-border animate-pulse h-full min-h-[200px]">
              <div className="h-3.5 bg-muted rounded w-3/4 mb-4"></div>
              <div className="h-7 bg-muted rounded w-1/2"></div>
            </div>
          ) : dashboardData?.tempoMedioResposta ? (
            <ResponseTimeCard
              tempoMedioResposta={dashboardData.tempoMedioResposta}
            />
          ) : isAtendimentoLoading ? (
            <div role="status" className="h-full min-h-[200px] animate-pulse rounded-2xl border border-border bg-card p-4 sm:p-5">
              <div className="mb-4 h-3.5 w-3/4 rounded bg-muted"></div>
              <div className="h-7 w-1/2 rounded bg-muted"></div>
              <span className="sr-only">Carregando indicadores de atendimento</span>
            </div>
          ) : atendimentoError ? (
            <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-card p-4 text-center">
              <p className="text-sm text-muted-foreground">Não foi possível carregar o tempo de resposta.</p>
              <button
                type="button"
                className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => refetchAtendimento()}
              >
                Tentar novamente
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Seção de gráficos — startDate/endDate são necessários para gerar todos os dias do período */}
      {isLoading && !dashboardData ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card rounded-2xl p-4 sm:p-6 border border-border animate-pulse h-80">
              <div className="h-4 bg-muted rounded w-1/3 mb-6"></div>
              <div className="h-56 bg-muted rounded-xl w-full"></div>
            </div>
          ))}
        </div>
      ) : (
        <DashboardCharts
          dashboardData={dashboardData}
          filteredRawLeads={[]}
          contexto={isBaseDataStale ? undefined : contexto}
          onAdClick={isBaseDataStale ? undefined : handleAdSpecificClick}
          onOrigemMetaClick={isBaseDataStale ? undefined : handleOrigemMetaClick}
          onLinkClick={isBaseDataStale ? undefined : handleLinkSpecificClick}
          adminMode={adminMode}
          targetClinicaId={targetClinicaId}
          startDate={startDate}
          endDate={endDate}
          isUpdating={isBaseDataStale}
          filtroSemanal={filtroSemanal}
          onFiltroSemanalChange={setFiltroSemanal}
        />
      )}

      {/* Tempo até fechar logo abaixo de "Links Personalizados" e "Distribuição por Dia da Semana" (pedido
          do usuário): mesmo bloco da aba Vendas, com consulta própria que não atrasa os KPIs do topo. */}
      {!isLoading && <SecaoTempoFechamentoDashboard
        clinicaId={adminMode ? targetClinicaId : clinicaId}
        inicio={startDate}
        fim={endDate}
        periodo={currentFilter}
        clinicaAdmin={adminMode ? targetClinicaId : undefined}
        apelidoDe={getAliasForAd} />}

      {/* Resultados por canal (anúncios, follow-up, disparos) depois dos gráficos: são consulta de apoio,
          não devem empurrar KPIs e gráficos para baixo. A seção some se o banco novo não estiver publicado. */}
      {!isLoading && (
        <SecaoResultadosMetricas
          contexto={contexto}
          periodoLabel={periodoExibido}
          onDetalharAnuncios={isBaseDataStale ? () => {} : handleLeadsAnunciosClick}
          onAbrirFilaPrioritaria={handlePriorityAction}
        />
      )}

      {/* Seção de insights e alertas */}
      {isLoading && !dashboardData ? (
        <div className="bg-card rounded-2xl p-4 sm:p-6 border border-border animate-pulse h-32">
          <div className="h-4 bg-muted rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-muted rounded w-full mb-2"></div>
          <div className="h-4 bg-muted rounded w-3/4"></div>
        </div>
      ) : (
        <DashboardInsights dashboardData={dashboardData} />
      )}

      {/* Conversão por origem com a explicação recolhida: referência ocasional, fica no fim da página. */}
      {dashboardData?.conversaoPorOrigem && (
        <ConversionAttributionNotice
          metrics={dashboardData.conversaoPorOrigem}
          periodLabel={periodoExibido}
        />
      )}

      {/* A consulta do modal usa a clínica exibida, inclusive no modo administrativo. */}
      {faturamentoModalOpen && <FalhaCarregamentoChunk>
        <Suspense fallback={<p role="status" className="text-sm text-muted-foreground">Abrindo faturamento…</p>}>
          <FaturamentoRealizadoModal
            clinicaId={adminMode ? targetClinicaId : clinicaId}
            inicio={startDate}
            fim={endDate}
            periodo={currentFilter}
            adminMode={adminMode}
            totalDashboard={dashboardData?.faturamentoRealizado}
            onClose={() => setFaturamentoModalOpen(false)}
          />
        </Suspense>
      </FalhaCarregamentoChunk>}

      {/* Só montar o detalhe aberto evita queries e código de chat na carga inicial. */}
      {contexto && !isBaseDataStale && (totalContatosModalOpen || leadsAnunciosModalOpen || adSpecificModalOpen || leadsLinksModalOpen || linkSpecificModalOpen) && (
        <LeadsListModal
          key={JSON.stringify([contexto, filtroDetalhe])}
          isOpen
          onClose={handleCloseModals}
          leads={[]}
          contexto={contexto}
          filtro={filtroDetalhe}
          totalContatos={totalContatosModalOpen ? dashboardData?.totalContatos
            : leadsAnunciosModalOpen ? dashboardData?.leadsAnuncios
            : leadsLinksModalOpen ? dashboardData?.leadsLinksPersonalizados : totalDoDetalhe}
          title={origemMetaSelecionada ? `${origemMetaSelecionada.dimensao === 'campanha' ? 'Leads da Campanha' : 'Leads do Conjunto'}: ${origemMetaSelecionada.nome}`
            : selectedAdName ? `Leads do Anúncio: ${getDisplayAdName(selectedAdName)}`
            : selectedLinkName ? `Leads do Link: ${selectedLinkName}`
            : leadsAnunciosModalOpen ? 'Leads de Anúncios'
            : leadsLinksModalOpen ? 'Leads de Links Personalizados' : totalContatosModalTitle}
          showAdColumn={leadsAnunciosModalOpen}
          // No total, cada linha precisa dizer se veio de anúncio ou de link; nos outros modais o título já filtra a origem.
          mostrarOrigem={totalContatosModalOpen}
          adminMode={adminMode}
          targetClinicaId={targetClinicaId}
        />
      )}

      {/* NOVO: Modal de Exportação PDF */}
      {isExportModalOpen && !isExportBlocked && <DashboardExportModal
        isOpen={isExportModalOpen}
        onClose={closeExportModal}
        selectedFormat={selectedFormat}
        onFormatChange={changeFormat}
        isExporting={isExporting}
        onExport={exportDashboard}
        dashboardData={dashboardData}
        filteredRawLeads={[]}
        clinicaName={getClinicaNameForExport()}
        periodo={currentFilter}
        startDate={startDate}
        endDate={endDate}
        generatedAt={generatedAt ?? new Date()}
        getAdDisplayName={getDisplayAdName}
      />}
    </div>
  );
};
